import { Component, Event, EventEmitter, h, Listen, Prop, State, Watch } from '@stencil/core';
import { Message } from '../../entities/auth';
import logger from '@phn/services/logger';
import featureToggles from '@phn/services/feature-toggles';
import { getEnvironment } from '@phn/entities/environment';
import { GlobalDataLayerService } from '@phn/services/analytics/global-data-layer-service';
import * as contentService from '@phn/services/content-service';
import { initAuthIframe, initializeUserData } from '@phn/services/user-service';
import windowService from '@phn/services/window-service';
import { navContentStore } from '@phn/state/nav-content-store';
import { navStateStore } from '@phn/state/nav-state-store';
import { shopStateStore, ShopStateStoreTypes } from '@phn/state/shop-state-store';
import { routerStore } from '@phn/state/router-store';
import { loadSavedDealerSearchResults } from '@phn/services/dealer-store-service';
import {
  DEFAULT_LOCALE,
  EventActions,
  IFRAME_ID,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType,
  VERSION
} from '@phn/utility/constants';
import { sanitizeApp } from '../../utility/helper';
import { initNaviMonitoring } from '@phn/services/monitoring';
import { isShopAvailable, loadsShopPropsOnStore } from '@phn/utility/helper/shop-helper';
import { Timeout } from '@phn/utility/timeout';
import abTestingService from '@phn/services/ab-testing/ab-testing-constructor-service';
import { NavigationDrawerScripts } from './navigation-drawer-scripts';
import { getConfig, isString } from '@phn/utility/helper';
import { Application, ApplicationType } from '@phn/utility/constants/app';

@Component({
  tag: 'phn-header',
  styleUrl: 'header.scss',
  shadow: true,
  assetsDirs: ['auth']
})
export class Header {
  @Prop() locale = DEFAULT_LOCALE;
  @Prop() env = '';
  @Prop() clientId = '';
  @Prop() pageName = '';
  @Prop() theme: NavigationThemeType = NavigationTheme.light;
  @Prop() mode: NavigationModeType = NavigationMode.navbar;
  @Prop() limitedWidth = 'true';
  @Prop() app: ApplicationType = Application.default;
  @Prop() initialRoutingPath = '';
  @Prop() displayLogoOnly = false;
  @Prop() numberOfShoppingItems = 0;
  @Prop() wishlistItemsNumber = 0;
  @Prop() shopInformation = '{ "wishlistItemsNumber": 0, "numberOfShoppingItems": 0, "subTotal": "0" }';

  @State() loadingContent = false;
  @State() receivedContent = false;
  @State() breakpoint = windowService.getBreakpoint();

  private eventPreventedTimeout = new Timeout(0);
  private checkedMarketingConsent = false;
  private loadedToggles = false;

  @Event({ composed: true }) navigationDidLoad!: EventEmitter<void>;
  @Event({ composed: true, bubbles: true, cancelable: true }) phnLogout!: EventEmitter<void>;
  @Event({ composed: true, bubbles: true, cancelable: true }) phnLogin!: EventEmitter<void>;

  @Watch('locale')
  async localeWatchHandler(): Promise<void> {
    navStateStore.state.locale = this.locale;
    await this.initNavContentStore();
    GlobalDataLayerService.getInstance().setLocale(this.locale);
    logger.init(this.breakpoint, this.locale);
  }

  @Watch('pageName')
  pageNameWatchHandler(): void {
    GlobalDataLayerService.getInstance().setPageName(this.pageName);
  }

  @Watch('env')
  async newEnvReceived(): Promise<void> {
    await this.initEnvironment();
    await this.initNavContentStore();

    initAuthIframe(getConfig(navStateStore.state.env));
  }

  @Watch('app')
  async appWatcher(newValue: string): Promise<void> {
    navStateStore.state.app = sanitizeApp(newValue);
  }

  @Watch('breakpoint')
  breakpointWatcher(): void {
    logger.init(this.breakpoint, this.locale);
  }

  @Watch('shopInformation')
  watchShopVariable(information: string, _: string, propName: keyof ShopStateStoreTypes): void {
    const shopInformation = JSON.parse(information);
    shopStateStore.set(propName, shopInformation);
  }

  @Watch('initialRoutingPath')
  initialRoutingPathWatcher(newRoutingPath: string): void {
    navStateStore.state.initialRoutingPath = newRoutingPath;
  }

  @Listen('resize', { target: 'window', passive: true })
  resizeHandler(): void {
    this.breakpoint = windowService.getBreakpoint();
    routerStore.state.breakpoint = windowService.getBreakpoint();
  }

  @Listen('message', { target: 'window', passive: true })
  async newAuthMessageReceived(event: Message): Promise<void> {
    const config = getConfig(navStateStore.state.env);

    if (new URL(config.LOGIN_REDIRECT_URL).origin !== new URL(event.origin).origin) {
      return;
    }

    if (isString(event.data.token)) {
      try {
        windowService.removeElement(IFRAME_ID);
        await initializeUserData(event.data.token, this.locale);
      } catch (err) {
        logger.error('INIT_USER_DATA', err as Error);
      }
    }
  }

  @Listen('loginEvent')
  async handleLogin(): Promise<void> {
    const phnLogin = this.phnLogin.emit();

    this.eventPreventedTimeout.set(async () => {
      if (phnLogin.defaultPrevented) {
        return;
      }

      const loginLink = navContentStore.state.myPorsche?.link;
      if (isString(loginLink)) {
        windowService.redirectToUrl(loginLink);
        return;
      }
    });
  }

  @Listen('logoutEvent')
  async handleLogout(): Promise<void> {
    const config = getConfig(navStateStore.state.env);

    const phnLogout = this.phnLogout.emit();

    this.eventPreventedTimeout.set(async () => {
      if (phnLogout.defaultPrevented) {
        return;
      }

      // TODO which logout url to use? myporsche? auth0?
      windowService.redirectToUrl(`${config.IDENTITY_PROVIDER_URL}/v2/logout`);
      return;
    });
  }

  @Listen('ucReady', { target: 'window', passive: true })
  handleUserCentricsReady(): void {
    this.checkMarketingConsent();
  }

  @Listen('ucInit', { target: 'window', passive: true })
  handleUserCentricsInitalization(): void {
    this.checkMarketingConsent();
  }

  @Listen('navigationLoaded')
  navigationLoadedHandler({ detail: navigationLoaded }: CustomEvent<boolean>): void {
    windowService.navigationLoaded = navigationLoaded;

    if (navigationLoaded) {
      this.navigationDidLoad.emit();
    }
  }

  private checkMarketingConsent() {
    if (!this.checkedMarketingConsent) {
      navStateStore.state.userConsent = false;
      this.checkedMarketingConsent = windowService.maybeCheckMarketingConsent(() => {
        navStateStore.state.userConsent = true;
      });
    }
  }

  private async initEnvironment() {
    navStateStore.state.env = getEnvironment(this.env);
    navStateStore.state.app = sanitizeApp(this.app);
    navStateStore.state.locale = this.locale;
    navStateStore.state.initialRoutingPath = this.initialRoutingPath;
    GlobalDataLayerService.getInstance().setEnvironment(navStateStore.state.env);
    featureToggles.setEnvironment(navStateStore.state.env);

    const config = getConfig(navStateStore.state.env);
    initAuthIframe(config);

    this.loadedToggles = false;
    await featureToggles.setFeatures(navStateStore.state.env);
    this.loadedToggles = true;
    initNaviMonitoring();

    logger.init(this.breakpoint, this.locale);
    const shopInformation = JSON.parse(this.shopInformation);

    loadsShopPropsOnStore({
      shopInformation: shopInformation
    });

    await abTestingService().init(this.app);
  }

  private async initNavContentStore() {
    const previouslyReceivedContent = this.receivedContent;
    this.receivedContent = false;
    this.loadingContent = true;
    try {
      const fetchedLocale = this.locale;
      const fetchedEnv = navStateStore.state.env;

      const contentForLocale = await contentService.fetchContent(this.app);

      // This is to make sure that the response is valid for the current locale and environment
      // and wasn't made with another locale or enironment
      if (this.locale === fetchedLocale && navStateStore.state.env === fetchedEnv) {
        Object.assign(navContentStore.state, contentForLocale);
        this.receivedContent = true;
      }
    } catch (err) {
      logger.error('FETCH_CONTENT', err as Error);

      if (previouslyReceivedContent) {
        this.receivedContent = true;
      }
    }

    this.loadingContent = false;
  }

  componentWillLoad(): void {
    GlobalDataLayerService.getInstance().setDefaultProperties(this.locale, this.pageName);
    (window as any).ncs = navContentStore;

    this.checkMarketingConsent();

    this.initEnvironment()
      .then(() => Promise.all([this.initNavContentStore(), loadSavedDealerSearchResults()]))
      .then(() => Promise.all([abTestingService().loadContentGeneratorExperiments(this.app)]));
  }

  async componentDidLoad(): Promise<void> {
    windowService.version = VERSION;
    GlobalDataLayerService.getInstance().pushDataLayerEvent(EventActions.GENERAL_LOAD);
    logger.info('NAVIGATION_LOAD');
  }

  disconnectedCallback(): void {
    this.eventPreventedTimeout.cancel();
  }

  private hasLimitedWidth(): boolean {
    return this.mode === NavigationMode.hero && this.limitedWidth === 'true' ? true : false;
  }

  render(): HTMLPhnHeaderElement {
    if (!this.loadedToggles) {
      return <div></div>;
    }

    return (
      // div needed because conditional styling does not work on Host of shadow component for some unknown reason
      <div class={this.hasLimitedWidth() ? 'header limited-width' : 'header'}>
        <NavigationDrawerScripts />

        <phn-wrapper
          theme={this.theme}
          mode={this.mode}
          locale={this.locale}
          loadingContent={this.loadingContent}
          receivedContent={this.receivedContent}
          breakpoint={this.breakpoint}
          app={sanitizeApp(this.app)}
          displayLogoOnly={this.displayLogoOnly}
          limitedWidth={this.hasLimitedWidth()}
        >
          {isShopAvailable(this.locale, sanitizeApp(this.app)) ? (
            <div slot="contextual_drawer_header">
              <slot name="contextual_drawer_header"></slot>
            </div>
          ) : null}
        </phn-wrapper>
      </div>
    );
  }
}
